<script setup>
/**
 * @description 通用确认弹窗组件
 * @param {boolean} visible - 是否显示
 * @param {string} title - 标题
 * @param {string} content - 内容
 * @param {string} type - 弹窗类型 ('info' | 'warning' | 'success' | 'error')
 * @param {boolean} showCancel - 是否显示取消按钮
 * @param {boolean} showOk - 是否显示确认按钮
 * @param {string} cancelText - 取消按钮文案
 * @param {string} okText - 确认按钮文案
 * @param {Function} beforeOk - 确认前回调（返回 false 阻止关闭）
 * @param {string} cancelType - 取消按钮 Arco 类型
 * @param {string} okType - 确认按钮 Arco 类型
 * @param {Object} cancelStyle - 取消按钮自定义样式
 * @param {Object} okStyle - 确认按钮自定义样式
 * @param {boolean} showIcon - 是否显示类型图标，默认 true；如修改金额等表单类弹窗可传 false
 * @param {number|string} width - 弹窗宽度
 * @param {boolean} closable - 是否显示右上角关闭按钮
 * @param {boolean} maskClosable - 是否允许点击蒙层关闭
 * @param {boolean} bottomSheetOnMobile - 移动端是否改为底部弹窗
 * @param {boolean} mobileFullscreen - 移动端是否全屏展示
 * @emits update:visible - 当显示状态改变时触发
 * @emits cancel - 点击取消按钮时触发
 * @emits ok - 点击确定按钮时触发
 * @emits close - 弹窗关闭时触发，带有关闭状态参数 { ok: boolean, cancel: boolean }
 * @slot default - 自定义内容插槽，位于 content 下方
 */

// 1. Imports
import { Button as AButton, Modal as AModal } from '@arco-design/web-vue'
import {
  IconCheckCircleFill,
  IconCloseCircleFill,
  IconExclamationCircleFill,
  IconInfoCircleFill,
} from '@arco-design/web-vue/es/icon'
import { computed, ref, watch } from 'vue'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { THEME_COLORS } from '~/utils/theme'

const typeConfigs = {
  info: {
    icon: IconInfoCircleFill,
    color: THEME_COLORS.info,
  },
  warning: {
    icon: IconExclamationCircleFill,
    color: THEME_COLORS.warning,
  },
  success: {
    icon: IconCheckCircleFill,
    color: THEME_COLORS.success,
  },
  error: {
    icon: IconCloseCircleFill,
    color: THEME_COLORS.danger,
  },
}

// 2. Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '提示',
  },
  content: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'info', // 'info' | 'warning' | 'success' | 'error'
  },
  showCancel: {
    type: Boolean,
    default: true,
  },
  showOk: {
    type: Boolean,
    default: true,
  },
  cancelText: {
    type: String,
    default: '取消',
  },
  okText: {
    type: String,
    default: '确定',
  },
  beforeOk: {
    type: Function,
    default: null,
  },
  cancelType: {
    type: String,
    default: 'secondary',
  },
  okType: {
    type: String,
    default: 'primary',
  },
  cancelStyle: {
    type: Object,
    default: () => ({}),
  },
  okStyle: {
    type: Object,
    default: () => ({}),
  },
  showIcon: {
    type: Boolean,
    default: true,
  },
  width: {
    type: [Number, String],
    default: 480,
  },
  closable: {
    type: Boolean,
    default: false,
  },
  maskClosable: {
    type: Boolean,
    default: true,
  },
  bottomSheetOnMobile: {
    type: Boolean,
    default: false,
  },
  mobileFullscreen: {
    type: Boolean,
    default: false,
  },
})

// 3. Emits
const emit = defineEmits(['update:visible', 'cancel', 'ok', 'close'])

// 4. Refs/Reactive State
const { isMobile } = useMediaQuery()
let hasEmittedClose = false
const okProcessing = ref(false)
const isMobileFullscreen = computed(() => {
  return isMobile.value && props.mobileFullscreen
})
const isMobileBottomSheet = computed(() => {
  return isMobile.value && props.bottomSheetOnMobile && !props.mobileFullscreen
})

const currentConfig = computed(() => {
  return typeConfigs[props.type] || typeConfigs.info
})
const modalStyle = computed(() => {
  if (isMobileFullscreen.value) {
    return { borderRadius: '0', overflow: 'hidden' }
  }

  if (isMobileBottomSheet.value) {
    return {
      borderRadius: '12px 12px 0 0',
      overflow: 'hidden',
      position: 'fixed',
      top: 'auto',
      right: '0',
      bottom: '0',
      left: '0',
      margin: '0',
      transform: 'none',
    }
  }

  return {
    borderRadius: isMobile.value ? '8px' : '12px',
    overflow: 'hidden',
    ...(isMobile.value ? {} : { top: '120px' }),
  }
})

// 5. Methods

/**
 * 统一处理关闭逻辑，确保 close 只触发一次
 */
const emitCloseOnce = (status) => {
  if (hasEmittedClose) return
  hasEmittedClose = true
  emit('close', status)
}

/**
 * 处理取消按钮点击
 */
const handleCancelClick = () => {
  if (okProcessing.value) return
  emit('cancel')
  emitCloseOnce({ ok: false, cancel: true })
  emit('update:visible', false)
}

/**
 * 处理确认按钮点击
 */
const handleOkClick = async () => {
  if (okProcessing.value) return
  okProcessing.value = true
  try {
    if (props.beforeOk) {
      const result = await props.beforeOk()
      if (result === false) return
    }
    emit('ok')
    emitCloseOnce({ ok: true, cancel: false })
    emit('update:visible', false)
  } catch (error) {
    return
  } finally {
    okProcessing.value = false
  }
}

/**
 * 处理遮罩/关闭按钮点击 (AModal 触发的 cancel)
 */
const handleModalClose = () => {
  if (okProcessing.value) return
  emitCloseOnce({ ok: false, cancel: false })
  emit('update:visible', false)
}

// 6. Watchers
// 处理外部直接修改 visible 的情况（例如父组件手动关闭）
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      hasEmittedClose = false // 重新打开时重置锁
    } else {
      emitCloseOnce({ ok: false, cancel: false })
    }
  }
)
</script>

<template>
  <component
    :is="AModal"
    :visible="visible"
    :footer="false"
    :closable="false"
    :mask-closable="maskClosable"
    :animation="false"
    :align-center="isMobile && !isMobileFullscreen && !isMobileBottomSheet"
    :fullscreen="isMobileFullscreen"
    :width="isMobileFullscreen || isMobileBottomSheet ? '100%' : isMobile ? '80%' : width"
    class="base-confirm-modal-wrapper"
    :class="{
      'is-mobile': isMobile,
      'is-mobile-fullscreen': isMobileFullscreen,
      'is-mobile-bottom-sheet': isMobileBottomSheet,
    }"
    :hide-title="true"
    :header="false"
    :modal-style="modalStyle"
    @cancel="handleModalClose"
  >
    <div
      class="base-confirm-modal"
      :class="{
        'is-mobile': isMobile,
        'is-mobile-fullscreen': isMobileFullscreen,
        'is-mobile-bottom-sheet': isMobileBottomSheet,
      }"
    >
      <div
        class="base-confirm-modal__message"
        :class="{
          'base-confirm-modal__message--no-icon': !showIcon,
          'base-confirm-modal__message--no-title': !title,
          'is-mobile': isMobile,
        }"
      >
        <component
          :is="currentConfig.icon"
          v-if="showIcon"
          class="base-confirm-modal__message-icon"
          :style="{ color: currentConfig.color }"
        />
        <div class="base-confirm-modal__message-text">
          <div v-if="title || closable" class="base-confirm-modal__header">
            <div v-if="title" class="base-confirm-modal__title">
              {{ title }}
            </div>
            <button
              v-if="closable"
              type="button"
              class="base-confirm-modal__close"
              aria-label="关闭"
              :disabled="okProcessing"
              @click="handleModalClose"
            >
              <SvgIcon name="close" size="24" aria-hidden="true" />
            </button>
          </div>
          <div v-if="content" class="base-confirm-modal__content-text">
            {{ content }}
          </div>
          <div v-if="$slots.default" class="base-confirm-modal__extra">
            <slot />
          </div>
        </div>
      </div>

      <div class="base-confirm-modal__footer">
        <AButton
          v-if="showCancel"
          :type="cancelType"
          class="base-confirm-modal__btn"
          :style="cancelStyle"
          :disabled="okProcessing"
          @click="handleCancelClick"
        >
          {{ cancelText }}
        </AButton>
        <AButton
          v-if="showOk"
          :type="okType"
          class="base-confirm-modal__btn"
          :style="okStyle"
          :loading="okProcessing"
          :disabled="okProcessing"
          @click="handleOkClick"
        >
          {{ okText }}
        </AButton>
      </div>
    </div>
  </component>
</template>

<style lang="scss" scoped>
.base-confirm-modal {
  &__message {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 32px 32px 24px;

    &--no-icon {
      display: block;
    }

    @include respond-to-max('lg') {
      padding: 24px 24px 16px;
      gap: 12px;
    }
  }

  &__message-icon {
    font-size: 24px;
    flex-shrink: 0;
    margin-top: 2px;

    @include respond-to-max('lg') {
      font-size: 20px;
      margin-top: 3px;
    }
  }

  &__message-text {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: flex-start;
    min-width: 0;
    min-height: 58px;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
  }

  &__title {
    flex: 1;
    min-width: 0;
    font-size: 20px;
    color: $text-title-color;
    font-weight: 700;
    line-height: 28px;

    @include respond-to-max('lg') {
      font-size: 18px;
    }
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    padding: 0;
    color: $text-subtitle-color;
    font-size: 24px;
    line-height: 1;
    background: transparent;
    border: 0;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: $text-title-color;
    }

    &:focus-visible {
      outline: 2px solid rgba($primary-color, 0.4);
      outline-offset: 2px;
      border-radius: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  &__content-text {
    font-size: 14px;
    line-height: 22px;
    color: $text-content-color;
    text-align: left;
    margin-top: 8px;

    @include respond-to-max('lg') {
      font-size: 14px;
      line-height: 22px;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 0 32px 32px;

    @include respond-to-max('lg') {
      padding: 0 16px 20px;
      gap: 12px;
    }
  }

  &__btn {
    height: 40px;
    padding: 0 16px;
    font-size: 16px;
    border-radius: 6px;
    transition: all 0.2s;
    min-width: 76px;

    &.arco-btn-secondary {
      background-color: transparent;
      color: $text-title-color;
      border: 1px solid $border-dashed-color;

      &:hover {
        background-color: $bg-color;
        border-color: $border-hover-color;
      }
    }

    &.arco-btn-primary {
      background-color: $primary-color;
      border: none;

      &:hover {
        background-color: rgba($primary-color, 0.9);
      }
    }
  }

  &.is-mobile {
    .base-confirm-modal__message {
      padding: 32px 24px 20px;
    }

    .base-confirm-modal__message-icon {
      font-size: 24px;
      margin-top: 2px;
    }

    .base-confirm-modal__title {
      font-size: 20px;
      font-weight: 700;
      line-height: 28px;
      color: $text-title-color;
    }

    .base-confirm-modal__content-text {
      font-size: 16px;
      line-height: 22px;
      color: $text-content-color;
    }

    .base-confirm-modal__footer {
      padding: 0 24px 24px;
      gap: 12px;
      justify-content: center;
    }

    .base-confirm-modal__btn {
      flex: 1;
      height: 44px;
      font-size: 16px;
    }
  }

  &.is-mobile-fullscreen {
    display: flex;
    flex-direction: column;
    height: 100dvh;

    .base-confirm-modal__message {
      padding: 20px 16px 12px;
      justify-content: flex-start;
      gap: 12px;
      border-bottom: 1px solid $border-dashed-color;
    }

    .base-confirm-modal__message-text {
      flex: 1;
      overflow-y: auto;
      min-height: 0;
    }

    .base-confirm-modal__content-text {
      text-align: left;
    }

    .base-confirm-modal__footer {
      padding: 0 16px calc(16px + env(safe-area-inset-bottom));
      justify-content: stretch;
    }

    .base-confirm-modal__btn {
      flex: 1;
    }
  }
}
</style>
<style lang="scss">
.base-confirm-modal-wrapper {
  .arco-modal-body {
    padding: 0;
    background-color: $bg-white;
  }
}

.base-confirm-modal-wrapper.is-mobile-fullscreen {
  width: 100vw;
  max-width: 100vw;
  margin: 0;
  top: 0;

  .arco-modal-body {
    height: 100dvh;
  }
}

.base-confirm-modal-wrapper.is-mobile-bottom-sheet {
  width: 100vw;
  max-width: 100vw;
  margin: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: none;

  .arco-modal-wrapper {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    height: 100%;
    overflow: hidden;
  }

  .arco-modal {
    top: auto;
  }

  .arco-modal-body {
    padding: 0;
  }
}
</style>
